import React, { Suspense, lazy, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useAuthContext } from '@/providers/auth-provider';
import { StreamProvider } from '@/providers/stream-provider';
import { fallback } from '@/components/fallback';
import { PrivateRoute } from './private-route';
import { paths } from '@/config/paths';
import { analytics } from '@/utils/analytics';
import { useAppSelector } from '@/stores';
import { selectUser } from '@/stores/selectors';
import { AppLayout } from '@/components/app-layout/app-layout';

// Lazy load views for code splitting
const LoginView = lazy(() => import('@/views/login-view/login-view').then(module => ({ default: module.LoginView })));
const CallbackView = lazy(() => import('@/views/callback-view/callback-view').then(module => ({ default: module.CallbackView })));
const AssistantView = lazy(() => import('@/views/assistant-view/assistant-view').then(module => ({ default: module.AssistantView })));
const AgentSelectorView = lazy(() => import('@/views/agent-selector-view/agent-selector-view').then(module => ({ default: module.AgentSelectorView })));
const TestCanvasView = lazy(() => import('@/views/test-canvas-view/test-canvas-view').then(module => ({ default: module.TestCanvasView })));
const TestEventOrchestrationView = lazy(() => import('@/views/test-event-orchestration-view/test-event-orchestration-view').then(module => ({ default: module.TestEventOrchestrationView })));
const TestIncidentWorkflowView = lazy(() => import('@/views/test-incident-workflow-view/test-incident-workflow-view').then(module => ({ default: module.TestIncidentWorkflowView })));
const ScrollTestView = lazy(() => import('@/views/scroll-test-view/scroll-test-view').then(module => ({ default: module.ScrollTestView })));
const ColorPaletteTest = lazy(() => import('@/components/color-palette-test').then(module => ({ default: module.ColorPaletteTest })));

// Best Practices views
const BestPracticesOverviewView = lazy(() => import('@/views/best-practices-overview-view/best-practices-overview-view').then(module => ({ default: module.BestPracticesOverviewView })));
const BestPracticesUsersView = lazy(() => import('@/views/best-practices-users-view/best-practices-users-view').then(module => ({ default: module.BestPracticesUsersView })));
const BestPracticesTeamsView = lazy(() => import('@/views/best-practices-teams-view/best-practices-teams-view').then(module => ({ default: module.BestPracticesTeamsView })));
const BestPracticesContactMethodsView = lazy(() => import('@/views/best-practices-contact-methods-view/best-practices-contact-methods-view').then(module => ({ default: module.BestPracticesContactMethodsView })));
const BestPracticesSchedulesView = lazy(() => import('@/views/best-practices-schedules-view/best-practices-schedules-view').then(module => ({ default: module.BestPracticesSchedulesView })));
const BestPracticesFlexibleSchedulesView = lazy(() => import('@/views/best-practices-flexible-schedules-view/best-practices-flexible-schedules-view').then(module => ({ default: module.BestPracticesFlexibleSchedulesView })));
const BestPracticesNotificationRulesView = lazy(() => import('@/views/best-practices-notification-rules-view/best-practices-notification-rules-view').then(module => ({ default: module.BestPracticesNotificationRulesView })));
const BestPracticesEscalationPoliciesView = lazy(() => import('@/views/best-practices-escalation-policies-view/best-practices-escalation-policies-view').then(module => ({ default: module.BestPracticesEscalationPoliciesView })));
const BestPracticesServicesView = lazy(() => import('@/views/best-practices-services-view/best-practices-services-view').then(module => ({ default: module.BestPracticesServicesView })));
const BestPracticesAIOpsView = lazy(() => import('@/views/best-practices-aiops-view/best-practices-aiops-view').then(module => ({ default: module.BestPracticesAIOpsView })));
const BestPracticesIncidentWorkflowsView = lazy(() => import('@/views/best-practices-incident-workflows-view/best-practices-incident-workflows-view').then(module => ({ default: module.BestPracticesIncidentWorkflowsView })));

// Navigation tracker component
const NavigationTracker = () => {
  const location = useLocation();
  const user = useAppSelector(selectUser);

  useEffect(() => {
    if (analytics.isInitialized() && user && location) {
      analytics.trackNavigation(location.pathname, {
        search: location.search,
        hash: location.hash,
        userId: user.userId,
        userEmail: user.email,
      });
    }
  }, [location, user]);

  return null;
};

// Conditional layout wrapper - applies AppLayout to all routes except /login and /assistant (agent selector)
const ConditionalLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === paths.Login;

  if (isLoginPage) {
    return <>{children}</>;
  }

  return <AppLayout>{children}</AppLayout>;
};

export const AppRouter: React.FC = () => {
  const { isAuthenticated, initializeAuthIsLoading } = useAuthContext();
  const FallbackComponent = fallback;

  if (initializeAuthIsLoading) {
    return <FallbackComponent message="Initializing application..." />;
  }

  return (
    <StreamProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <NavigationTracker />
        <ConditionalLayout>
          <Suspense fallback={<FallbackComponent message="Loading content..." />}>
            <Routes>
              {/* Public Routes */}
              <Route path={paths.Login} element={<LoginView />} />
              <Route path={paths.Callback} element={<CallbackView />} />

              {/* Protected Routes */}
              <Route
                path={paths.Assistant}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <AssistantView />
                  </PrivateRoute>
                }
              />

              {/* Test Canvas Route - Public for easy testing */}
              <Route path={paths.TestCanvas} element={<TestCanvasView />} />

              {/* Test Event Orchestration Route - Public for testing Phase 5 */}
              <Route path={paths.TestEventOrchestration} element={<TestEventOrchestrationView />} />

              {/* Test Incident Workflow Route - Public for testing Incident Workflow Preview UI */}
              <Route path={paths.TestIncidentWorkflow} element={<TestIncidentWorkflowView />} />

              {/* Test Scroll Route - Public for testing vertical scroll */}
              <Route path={paths.TestScroll} element={<ScrollTestView />} />

              {/* Color Palette Test Route - Public for viewing custom colors */}
              <Route path={paths.ColorPalette} element={<ColorPaletteTest />} />

              {/* Best Practices Routes */}
              <Route
                path={paths.BestPracticesOverview}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesOverviewView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesUsers}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesUsersView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesTeams}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesTeamsView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesContactMethods}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesContactMethodsView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesSchedules}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesSchedulesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesFlexibleSchedules}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesFlexibleSchedulesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesNotificationRules}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesNotificationRulesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesEscalationPolicies}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesEscalationPoliciesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesServices}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesServicesView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesAIOps}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesAIOpsView />
                  </PrivateRoute>
                }
              />
              <Route
                path={paths.BestPracticesIW}
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <BestPracticesIncidentWorkflowsView />
                  </PrivateRoute>
                }
              />

              {/* Redirect /best-practices to /best-practices/overview */}
              <Route
                path={paths.BestPractices}
                element={<Navigate to={paths.BestPracticesOverview} replace />}
              />

              {/* Root redirect */}
              <Route
                path={paths.Root}
                element={<Navigate to={isAuthenticated ? paths.Assistant : paths.Login} replace />}
              />

              {/* Fallback to login for any unknown routes */}
              <Route path="*" element={<Navigate to={paths.Login} replace />} />
            </Routes>
          </Suspense>
        </ConditionalLayout>
      </Router>
    </StreamProvider>
  );
};

export default AppRouter;
